/**
 * Centralized error translation utility.
 * Maps Firebase/Stripe error codes to user-friendly messages.
 * Never exposes raw error.message or internal codes to users.
 */

const ERROR_MAP = {
  // Firebase Auth
  'auth/user-not-found': 'No account found with this email.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/invalid-credential': 'Invalid email or password. Please try again.',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/account-exists-with-different-credential': 'An account already exists with this email. Please sign in with email and password instead.',
  'auth/email-already-in-use': 'An account already exists with this email. Please sign in instead.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled. Please try again.',
  'auth/cancelled-popup-request': 'Only one sign-in popup can be open at a time. Please try again.',
  'auth/popup-blocked': 'Your browser blocked the sign-in popup. Please allow popups for this site and try again.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/weak-password': 'Password is too weak. Please use at least 6 characters.',
  'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/network-request-failed': 'Network error. Please check your connection and try again.',
  'auth/requires-recent-login': 'For security, please sign out and sign back in to complete this action.',

  // Firestore / Cloud Functions
  'permission-denied': 'You don\'t have permission to perform this action.',
  'not-found': 'The requested resource was not found.',
  'already-exists': 'This resource already exists.',
  'resource-exhausted': 'Service is temporarily busy. Please try again in a moment.',
  'functions/resource-exhausted': 'Service is temporarily busy. Please try again in a moment.',
  'unavailable': 'Service is temporarily unavailable. Please try again.',
  'deadline-exceeded': 'The request took too long. Please try again.',
  'unauthenticated': 'Please sign in to continue.',
  'failed-precondition': 'This action cannot be completed right now. Please try again.',
  'internal': 'Something went wrong on our end. Please try again.',
};

/**
 * Translate a Firebase/API error into a user-friendly message.
 * @param {Error|object} error - The caught error object
 * @param {string} [fallback] - Optional custom fallback message
 * @returns {string} A human-readable error message
 */
export function friendlyError(error, fallback) {
  if (!error) return fallback || 'Something went wrong. Please try again.';

  // Firebase errors use error.code
  const code = error.code || '';
  if (code && ERROR_MAP[code]) {
    return ERROR_MAP[code];
  }

  // Some Firebase functions errors have code like 'functions/resource-exhausted'
  if (code.startsWith('functions/')) {
    const funcCode = code;
    if (ERROR_MAP[funcCode]) return ERROR_MAP[funcCode];
    // Try without prefix
    const shortCode = code.replace('functions/', '');
    if (ERROR_MAP[shortCode]) return ERROR_MAP[shortCode];
  }

  return fallback || 'Something went wrong. Please try again.';
}
